// components/DoorSplit.js
//
// Interactieve "voor/achter"-splitfoto: sleep de ronde knop om tussen een
// binnen- (INSIDE) en buitenaanzicht (Nuki smart lock + keypad) te schuiven,
// met een schuine scheidingslijn i.p.v. een rechte - zoals Nuki's eigen
// marketingsite dat doet. Werkt met percentages i.p.v. de vaste
// 1920px-pixelcoördinaten uit het oorspronkelijke prototype, zodat de schuine
// lijn correct blijft schalen op elke schermbreedte i.p.v. enkel te kloppen
// bij een exact 1920px brede container.
import React, { useRef, useState, useCallback, useEffect } from 'react';
import './DoorSplit.css';

const ASPECT_W = 1920;
const ASPECT_H = 960;
// Schuinte van de scheidingslijn, uitgedrukt als % van de breedte (was 110px
// t.o.v. de 1920px-referentiebreedte uit het prototype).
const SLANT_PCT = (110 / ASPECT_W) * 100;
// Symmetrisch rond het midden (was 15-65, dus rechts maar 15% i.p.v. 35%
// zoals links - hierdoor kon je veel minder ver naar rechts slepen).
const MIN_PCT = 15;
const MAX_PCT = 85;

function DoorSplit() {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [pct, setPct] = useState(50);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(MIN_PCT, Math.min(MAX_PCT, p));
    setPct(p);
  }, []);

  // Slepen kan enkel via de ronde knop zelf: klikken/tikken ergens anders op
  // de foto verplaatst de schuif niet.
  const handleMouseDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
  };

  const handleTouchStart = () => {
    draggingRef.current = true;
  };

  // Mousemove/-up en touchmove/-end op window (i.p.v. enkel op de container)
  // zodat het slepen niet stopt zodra de cursor/vinger buiten de foto komt -
  // zelfde patroon als de fase-carousel drag-logica elders in dit project.
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (draggingRef.current) updateFromClientX(e.clientX);
    };
    const handleMouseUp = () => { draggingRef.current = false; };
    const handleTouchMove = (e) => {
      if (draggingRef.current) updateFromClientX(e.touches[0].clientX);
    };
    const handleTouchEnd = () => { draggingRef.current = false; };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [updateFromClientX]);

  const xTopPct = pct + SLANT_PCT;
  const xBottomPct = pct - SLANT_PCT;
  // De SVG-lijn gebruikt wel de originele 1920x960-viewBox-eenheden, want een
  // SVG met preserveAspectRatio="none" schaalt die vanzelf correct mee naar de
  // werkelijke afmetingen van de container - dat hoeft dus niet in % te zijn.
  const xTopViewBox = (pct / 100) * ASPECT_W + 110;
  const xBottomViewBox = (pct / 100) * ASPECT_W - 110;

  return (
    <div
      ref={containerRef}
      className="door-split"
    >
      {/* Basislaag (onbeperkt, blijft zichtbaar rechts van de scheidingslijn)
          = buitenaanzicht. De linkerkant toont het binnenaanzicht via de
          geknipte laag hieronder. */}
      <div className="door-split-layer door-split-outside">
        <img src="/outside.png" alt="Outside" />
      </div>

      {/* Geknipte laag, dekt enkel het linker gedeelte af (0 tot de
          scheidingslijn) = binnenaanzicht - zo staat inside links en
          outside rechts. */}
      <div
        className="door-split-layer door-split-inside"
        style={{ clipPath: `polygon(0 0, ${xTopPct}% 0, ${xBottomPct}% 100%, 0 100%)` }}
      >
        <img src="/inside.png" alt="Inside" />
      </div>

      <svg
        className="door-split-divider"
        viewBox={`0 0 ${ASPECT_W} ${ASPECT_H}`}
        preserveAspectRatio="none"
      >
        <line x1={xTopViewBox} y1="0" x2={xBottomViewBox} y2={ASPECT_H} />
      </svg>

      <div
        className="door-split-handle"
        style={{ left: `${pct}%` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="door-split-handle-inner">
          <span className="door-split-arrow door-split-arrow-left" />
          <span className="door-split-arrow door-split-arrow-right" />
        </div>
      </div>
    </div>
  );
}

export default DoorSplit;
